import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import { filterTruthy } from "@redotech/util/array";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { ExtendedAddress } from "../address";
import { checkoutShippingOptionSchema } from "../checkouts/checkout-shipping-options";
import { LockedLandedCostSchema } from "../landed-cost/rate-landed-cost";
import { Provider } from "../order";
import { ItemOrderBy } from "../outbound-labels";
import {
  ContentsType,
  NonDeliveryOption,
  USPSExemptionCode,
} from "../outbound-labels/customs";
import {
  LearnedPresetKind,
  LearnedPresetSchema,
} from "../outbound-labels/learned-preset";
import {
  CanadaPostOption,
  IB_CARRIER_ACCOUNT_ID,
  IShipmentPaymentOption,
  OutboundRate,
  RateProvider,
  ShipmentMessageSchema,
  ShipmentPaymentOptionSchema,
  ShipmentRatesSchema,
  SpecialRateEligibility,
} from "../outbound-labels/outbound-labels";
import {
  DEFAULT_REDO_PARCEL,
  getParcelWeightGrams,
  OutboundParcelCustomsSchema,
  OutboundParcelSchema,
  Parcel,
} from "../outbound-labels/parcel";
import {
  addWeight,
  convertWeight,
  MoneySchema,
  Weight,
  WeightSchema,
  WeightUnit,
} from "../outbound-labels/util";
import { OrderItemTableColumnData } from "../packing-slips/packing-slip-builder";
import { TagSchema } from "../tag";
import { CustomsDeclarationSchema } from "./customs-declaration";
import {
  Carriers,
  carrierToLabel,
  SelectedShippingRate,
  SelectedShippingRateSchema,
  ServiceSchema,
  servicesMatch,
} from "./fulfillment-carriers-and-services";
import { RedoShopifyDeliveryMethodType } from "./fulfillment-delivery-method-type";
import { IOMSEvent, OMSEventSchema } from "./fulfillment-group-events";
import { FulfillmentGroupStatus } from "./fulfillment-group-status";
import { FulfillmentHoldSchema } from "./fulfillment-hold";
import {
  LocationForMoveSchema,
  RedoFulfillmentLocationSchema,
} from "./fulfillment-location";
import { RedoShopifyFulfillmentOrderAction } from "./fulfillment-order-action";
import {
  FulfillmentOrderAddressSchema,
  IFulfillmentOrderAddress,
  IVerifiedAddress,
  VerifiedAddressSchema,
} from "./fulfillment-order-address";
import {
  expandLineItemsToPickableItems,
  FulfillmentOrderLineItem,
  FulfillmentOrderLineItemSchema,
  FulfillmentOrderLineItemSchema_,
  IFulfillmentOrderLineItem,
} from "./fulfillment-order-line-item";
import { RedoShopifyFulfillmentOrderRequestStatus } from "./fulfillment-order-request-status";
import { RedoShopifyFulfillmentOrderStatus } from "./fulfillment-order-status";
import { PrintStatusSchema } from "./fulfillment-print-status";
import { RedoFulfillmentToteSchema } from "./fulfillment-tote";
import { Hazmat } from "./hazmat";
import {
  IMarketplaceNotificationStatus,
  MarketplaceNotificationStatusSchema,
} from "./marketplace-notification-status";
import { IOMSUser, OMSUserSchema } from "./oms-user";
import { RedoShopifyOrderDisplayFinancialStatus } from "./order-display-financial-status";
import {
  RedoShopifyOrderDisputeInitiatedAs,
  RedoShopifyOrderDisputeStatus,
} from "./order-disputes";
import { OrderTransactionKind } from "./order-transaction-kind";
import {
  IRedoFulfillment,
  IShipmentPackage,
  OmsInsuranceProvider,
  RecordedShipmentSchema,
  RedoFulfillmentSchema,
  ShipmentDetailsSchema,
  ShipmentPackage,
  ShipmentPackageSchema,
  ShipmentTrackerSchema,
} from "./outbound-shipment-label";
import {
  ItemParcelAssignmentsSchema,
  PackingParcelsSchema,
} from "./packing-parcels";
import { PlacedOnHoldSource } from "./placed-on-hold-source";
import {
  SelectedShippingRateSetBy,
  SelectedShippingRateSetByInfoSchema,
} from "./selected-shipping-rate-set-by";
import {
  FulfillmentOrderSupportedActionSchema,
  IRisk,
  RiskSchema,
} from "./shopify-fulfillment-schemas";

// This defines the default range for competitive spread. By default, we
// will beat the opposing rate by a pseudo random value between 1 - 10 cents.
export const DEFAULT_COMP_SPREAD_BEAT_RATE_BY = 0.1;
export const DEFAULT_COMP_SPREAD_MIN_BEAT_RATE_BY = 0.01;

// Do not change without approval from leadership
export const DEFAULT_COMP_SPREAD_CARRIER_OVERRIDES = [
  { carriers: [Carriers.DHL_ECS], minBeatRateBy: 0.1, maxBeatRateBy: 0.3 },
];

export enum ScanAction {
  SCAN = "scan",
  UNSCAN = "unscan",
}

export const FulfillmentGroupCustomsSchema = z.object({
  declarations: z.array(CustomsDeclarationSchema),
  overwritten: z.boolean(),
});
export type FulfillmentGroupCustoms = z.infer<
  typeof FulfillmentGroupCustomsSchema
>;
export interface IFulfillmentGroupCustoms
  extends z.ZodType<FulfillmentGroupCustoms> {}

export const LabelErrorSchema = z.object({ message: z.string() });
export type LabelError = z.infer<typeof LabelErrorSchema>;

export const OrderTransactionSchema = z.object({
  id: z.string(),
  formattedGateway: z.string().nullish(),
  gateway: z.string().nullish(),
  paymentDetails: z
    .object({
      company: z.string().nullish(),
      number: z.string().nullish(),
      lastFour: z.string().nullish(),
    })
    .nullish(),
  amount: MoneySchema,
  parentTransactionId: z.string().nullish(),
  kind: createEnum(OrderTransactionKind, { enumName: "OrderTransactionKind" }),
});
export type OrderTransaction = z.infer<typeof OrderTransactionSchema>;
export interface IOrderTransaction extends z.ZodType<OrderTransaction> {}

export const OrderDisputeSchema = z.object({
  id: z.string(),
  status: createEnum(RedoShopifyOrderDisputeStatus, {
    enumName: "RedoShopifyOrderDisputeStatus",
  }),
  initiatedAs: createEnum(RedoShopifyOrderDisputeInitiatedAs, {
    enumName: "RedoShopifyOrderDisputeInitiatedAs",
  }),
});
export type OrderDispute = z.infer<typeof OrderDisputeSchema>;
export interface IOrderDispute extends z.ZodType<OrderDispute> {}

export const FulfillmentOrderCustomerSchema = z.object({
  id: z.string(),
  name: z.string(),
  tags: z.array(z.string()).nullish(),
  numOrders: z.number().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  note: z.string().nullish(),
  localizations: z.array(z.string()).nullish(),
});

export type FulfillmentOrderCustomer = z.infer<
  typeof FulfillmentOrderCustomerSchema
>;

//https://lorefnon.me/2023/11/28/fixing-inferred-types-exceeding-serializable-length/
export interface IFulfillmentOrderCustomer
  extends z.ZodType<FulfillmentOrderCustomer> {}

export const FulfillmentOrderOrderSchema = z.object({
  id: z.string(),
  redoOrderId: z.string().nullish(),
  fullyPaid: z.boolean().nullish(),
  displayFinancialStatus: createEnum(RedoShopifyOrderDisplayFinancialStatus, {
    enumName: "RedoShopifyOrderDisplayFinancialStatus",
  }).nullish(),
  discountCodes: z.array(z.string()).nullish(),
  name: z.string(),
  secondaryOrderName: z.string().nullish(),
  risk: RiskSchema.nullish(),
  tags: z.array(z.string()).nullish(),
  note: z.string(),
  customAttributes: z
    .array(z.object({ key: z.string(), value: z.string().nullish() }))
    .nullish(),
  createdAt: z.date(),
  shippingCost: MoneySchema,
  shippingCostPresented: MoneySchema.nullish(),
  shippingTaxLines: z.array(z.object({ priceSet: MoneySchema })).nullish(),
  closed: z.boolean().nullish(),
  closedAt: z.date().nullish(),
  salesChannel: z.string().nullish(),
  channelInformation: z
    .object({ app: z.object({ developerName: z.string().nullish() }) })
    .nullish(),
  internalNote: z.string().nullish(),
  packageProtected: z.boolean().nullish(),
  customDate1: z.date().nullish(),
  customDate2: z.date().nullish(),
  customDate3: z.date().nullish(),
  customString1: z.string().nullish(),
  customString2: z.string().nullish(),
  customString3: z.string().nullish(),
  customNumber1: z.number().nullish(),
  customNumber2: z.number().nullish(),
  customNumber3: z.number().nullish(),
  customAttributeDate1: z.date().nullish(),
  customAttributeDate2: z.date().nullish(),
  customAttributeDate3: z.date().nullish(),
  customAttributeString1: z.string().nullish(),
  customAttributeString2: z.string().nullish(),
  customAttributeString3: z.string().nullish(),
  customAttributeNumber1: z.number().nullish(),
  customAttributeNumber2: z.number().nullish(),
  customAttributeNumber3: z.number().nullish(),
  disputes: z.array(OrderDisputeSchema as IOrderDispute).nullish(),
});
export type FulfillmentOrderOrder = z.infer<typeof FulfillmentOrderOrderSchema>;
export interface IFulfillmentOrderOrder
  extends z.ZodType<FulfillmentOrderOrder> {}

export const FulfillmentOrderSchema = z.object({
  provider: createEnum(Provider, { enumName: "Provider" }),
  externalId: z.string(), // SHOULD BE GLOBALLY UNIQUE
  externalSearchId: z.string().nullish(),
  subsidiaryId: z.string().nullish(),
  billingAddress: (
    FulfillmentOrderAddressSchema as IFulfillmentOrderAddress
  ).nullish(),
  originAddress: FulfillmentOrderAddressSchema as IFulfillmentOrderAddress,
  verifiedOriginAddress: (VerifiedAddressSchema as IVerifiedAddress).nullish(),
  deliveryMethod: z.object({
    name: z.string().nullish(),
    code: z.string().nullish(),
    kind: createEnum(RedoShopifyDeliveryMethodType, {
      enumName: "RedoShopifyDeliveryMethodType",
    }),
    instructions: z.string().nullish(),
    phone: z.string().nullish(),
    selectedRedoCheckoutShippingOptions: z
      .array(checkoutShippingOptionSchema)
      .nullish(),
  }),
  destinationAddress: FulfillmentOrderAddressSchema as IFulfillmentOrderAddress,
  verifiedDestinationAddress: (
    VerifiedAddressSchema as IVerifiedAddress
  ).nullish(),
  createdAt: z.date(),
  updatedAt: z.date(),
  fulfillAt: z.date().nullish(),
  fulfillBy: z.date().nullish(),
  /**
   * WARNING: Line items may contain sub items from Bill of Materials (BOM) expansion.
   * If the use case for accessing the line items is related to picking items (packing slips, pick lists, scanning, pick sessions, etc) - you probably want to use `expandLineItemsToPickableItems(lineItems)` to get only the leaf node items.
   * In most cases (splitting, table filtering, etc) - you probably want to use the top level line items - the actual line items that were ordered.
   */
  lineItems: z.array(
    FulfillmentOrderLineItemSchema as IFulfillmentOrderLineItem,
  ),
  transactions: z.array(OrderTransactionSchema as IOrderTransaction).nullish(),
  locationsForMove: z.array(LocationForMoveSchema).nullish(),
  supportedActions: z.array(FulfillmentOrderSupportedActionSchema).nullish(),
  order: (FulfillmentOrderOrderSchema as IFulfillmentOrderOrder).nullish(),
  customer: (
    FulfillmentOrderCustomerSchema as IFulfillmentOrderCustomer
  ).nullish(),
  requestStatus: createEnum(RedoShopifyFulfillmentOrderRequestStatus, {
    enumName: "RedoShopifyFulfillmentOrderRequestStatus",
  }).nullish(),
  status: createEnum(RedoShopifyFulfillmentOrderStatus, {
    enumName: "RedoShopifyFulfillmentOrderStatus",
  }),
  /** Reference to an inventory transfer that this fulfillment order was created from */
  transferId: z.string().nullish(),
  /** Fulfillment holds synced from the provider. Read-only — written only by the sync. */
  holds: z.array(FulfillmentHoldSchema).nullish(),
});

export type FulfillmentOrder = z.infer<typeof FulfillmentOrderSchema>;
export interface IFulfillmentOrder extends z.ZodType<FulfillmentOrder> {}

const ShippingCostBreakdownSchema = z
  .object({
    fulfillmentOrdersBreakdown: z
      .array(
        z.object({
          labelCost: z.string(),
          markup: z
            .object({
              percentage: z.string().optional(),
              amount: z.string().optional(),
            })
            .optional(),
        }),
      )
      .optional(),
  })
  .merge(MoneySchema);
export type ShippingCostBreakdown = z.infer<typeof ShippingCostBreakdownSchema>;

export enum OverrideAddressSourceType {
  AUTO_MERGE = "auto_merge",
  MERGE = "merge",
  MANUAL = "manual",
}

export const FulfillmentGroupAddressesSchema = z.object({
  address: FulfillmentOrderAddressSchema,
  verifiedAddress: VerifiedAddressSchema.nullish(),
  sourceType: createEnum(OverrideAddressSourceType, {
    enumName: "OverrideAddressSourceType",
  }).nullish(),
});
export type FulfillmentGroupAddresses = z.infer<
  typeof FulfillmentGroupAddressesSchema
>;
export interface IFulfillmentGroupAddresses
  extends z.ZodType<FulfillmentGroupAddresses> {}

export enum IntegrationType {
  TRACKSTAR = "trackstar",
}

export const BinSchema = z.object({
  bin: z.string(),
  quantity: z.number().nullish(),
});

export type Bin = z.infer<typeof BinSchema>;

export const LineItemLocationSchema = z.object({
  sku: z.string(),
  bins: z.array(BinSchema).nullish(),
});

export type LineItemLocation = z.infer<typeof LineItemLocationSchema>;

export interface ILineItemLocation extends z.ZodType<LineItemLocation> {}

export const TrackstarIntegrationReferenceSchema = z.object({
  integrationType: z.literal(IntegrationType.TRACKSTAR),
  shipmentFulfillmentPairs: z.array(
    z.object({
      fulfillmentOrderId: z.string(),
      shipmentId: z.string().nullish(),
      lineItemLocations: z
        .array(LineItemLocationSchema as ILineItemLocation)
        .nullish(),
    }),
  ),
});

export enum FulfilledBy {
  REDO = "Redo",
  EXTERNAL = "External",
}

export enum ProductRecommendationsSource {
  AI = "ai",
  FALLBACK = "fallback",
  NONE = "none",
}

export const FulfillmentInfoSchema = z.object({
  carrier: createEnum(Carriers, { enumName: "Carriers" }).nullish(),
  service: ServiceSchema.nullish(),
  trackingCode: z.string().nullish(),
  shipDate: z.date().nullish(),
  numericRate: z.number().nullish(),
  estimatedDeliveryDays: z.number().nullish(),
  tracker: ShipmentTrackerSchema.nullish(),
});
export type FulfillmentInfo = z.infer<typeof FulfillmentInfoSchema>;
export interface IFulfillmentInfo extends z.ZodType<FulfillmentInfo> {}

export const FulfillmentGroupSummarySchema = z.object({
  /**
   * WARNING: If the use case is related to picking items (packing slips, pick lists, scanning, pick sessions, etc) - you probably want to use `expandedItemQuantity` instead.
   * In most cases (order totals, reporting, etc) - you probably want to use this field - the total quantity of items that were ordered.
   */
  totalItemQuantity: z.number(),
  /**
   * The number of items after Bill of Materials (BOM) expansion - only counts leaf-node items (actual components to ship/pick).
   * If the use case is related to picking items (packing slips, pick lists, scanning, pick sessions, etc) - you probably want to use this field.
   * In most cases (order totals, reporting, etc) - you probably want to use `totalItemQuantity` - the total quantity of items that were ordered.
   * Falls back to totalItemQuantity if no BOM expansion exists.
   */
  expandedItemQuantity: z.number().nullish(),
  totalSkuCount: z.number(),
  totalLineItemCount: z.number().nullish(),
  groupedProductCount: z.number().nullish(),
  groupedProductWithAttributesCount: z.number().nullish(),
  totalWeightGrams: z.number(),
  totalWeightWithParcelGrams: z.number().nullish(),
  displayWeightGrams: z.number().nullish(),
  totalPrice: MoneySchema,
  totalDiscountedPrice: MoneySchema.nullish(),
  totalPriceInt: z.number().nullish(),
  totalTax: MoneySchema,
  totalTaxInt: z.number().nullish(),
  totalShippingCost: ShippingCostBreakdownSchema,
  totalShippingCostInt: z.number().nullish(),
  providerCreatedAt: z.date(),
  providerUpdatedAt: z.date(),
  updatedAt: z.date().nullish(),
  deliveryMethod: createEnum(RedoShopifyDeliveryMethodType, {
    enumName: "RedoShopifyDeliveryMethodType",
  }).nullish(),
  originAddress: FulfillmentOrderAddressSchema as IFulfillmentOrderAddress,
  verifiedOriginAddress: (VerifiedAddressSchema as IVerifiedAddress).nullish(),
  destinationAddress: FulfillmentOrderAddressSchema as IFulfillmentOrderAddress,
  verifiedDestinationAddress: (
    VerifiedAddressSchema as IVerifiedAddress
  ).nullish(),
  billingAddress: (
    FulfillmentOrderAddressSchema as IFulfillmentOrderAddress
  ).nullish(),
  customer: (
    FulfillmentOrderCustomerSchema as IFulfillmentOrderCustomer
  ).nullish(),
  orderCreatedAt: z.date(),
  lineItemLocations: z
    .array(LineItemLocationSchema as ILineItemLocation)
    .nullish(),
  fulfillmentInfo: (FulfillmentInfoSchema as IFulfillmentInfo).nullish(),
  batchSuggestionKey: z.string().nullish(),
  fulfilledBy: createEnum(FulfilledBy, { enumName: "FulfilledBy" }).nullish(),
  fulfillmentOrderCount: z.number().nullish(),
  ratesOutdated: z.boolean().nullish(),
  checkoutPromisedDeliveryDate: z.date().nullish(),
  needsTrackingSync: z.boolean().nullish(),
  hasMissingBins: z.boolean().nullish(),
  returnShipmentOverrides: z
    .object({
      shopperAddress: FulfillmentOrderAddressSchema.nullish(),
      merchantAddress: FulfillmentOrderAddressSchema.nullish(),
      parcel: OutboundParcelSchema.nullish(),
    })
    .nullish(),
  totalDimValue: z.number().nullish(),
  usedLearning: z
    .object({
      [LearnedPresetKind.CARRIER_SERVICE_LEVEL]: z.boolean(),
      [LearnedPresetKind.PARCEL]: z.boolean(),
      [LearnedPresetKind.WEIGHT]: z.boolean(),
    })
    .nullish(),
  /**
   * Denormalized product info including BOM-expanded product IDs.
   * Needed because top-level lineItems only contain parent product IDs —
   * BOM child product IDs are nested in billOfMaterialsInfos recursively.
   * This means we can't add an index on that directly, so we denormalize it here.
   */
  productInfo: z.object({ productIds: z.array(z.string()) }).nullish(),
});

export type FulfillmentGroupSummary = z.infer<
  typeof FulfillmentGroupSummarySchema
>;
export interface IFulfillmentGroupSummary
  extends z.ZodType<FulfillmentGroupSummary> {}

export enum SortingCriteria {
  FASTEST = "FASTEST",
  CHEAPEST = "CHEAPEST",
  CHEAPEST_WITHIN_DELIVERY_DAYS = "CHEAPEST_WITHIN_DELIVERY_DAYS",
}

export const RateSelectionCriteria = z.object({
  ratesToChooseFrom: z.array(SelectedShippingRateSchema),
  sortingCriteria: createEnum(SortingCriteria, { enumName: "SortingCriteria" }),
  ratesToPrioritize: z.array(SelectedShippingRateSchema).nullish(),
  maxPriorityPriceDifference: z.number().nullish(),
  targetDeliveryDays: z.number().nullish(),
});
export type RateSelectionCriteria = z.infer<typeof RateSelectionCriteria>;

export type TrackstarIntegrationReference = z.infer<
  typeof TrackstarIntegrationReferenceSchema
>;

export const IntegrationReferenceSchema = z.discriminatedUnion(
  "integrationType",
  [TrackstarIntegrationReferenceSchema],
);

export type IntegrationReference = z.infer<typeof IntegrationReferenceSchema>;

export interface IIntegrationReference
  extends z.ZodType<IntegrationReference> {}

export enum ReturnShipmentStatus {
  RETURN_LABEL_PRE_CHARGE = "RETURN_LABEL_PRE_CHARGE",
  RETURN_LABEL_CHARGED = "RETURN_LABEL_CHARGED",
  RETURN_LABEL_VOIDED = "RETURN_LABEL_VOIDED",
}

export enum AddressFixedBy {
  ADDRESS_COMPARISON_AGENT = "ADDRESS_COMPARISON_AGENT",
  ADDRESS_FIXING_AGENT = "ADDRESS_FIXING_AGENT",
  CUSTOMER_AFTER_EMAIL = "CUSTOMER_AFTER_EMAIL",
}

export const GroupSelectedRateSchema = SelectedShippingRateSchema.extend({
  rate: z.number().nullish(),
});
export type GroupSelectedRate = z.infer<typeof GroupSelectedRateSchema>;
export interface IGroupSelectedRate extends z.ZodType<GroupSelectedRate> {}

export enum FulfillmentGroupPickStatus {
  PICKED = "PICKED",
  PROBLEM = "PROBLEM",
  IN_PROGRESS = "IN_PROGRESS",
}

const PickSessionItemStatusSchema = z.object({
  fulfillmentOrderLineItemExternalId: z.string(),
  quantityPicked: z.number(),
});
export type PickSessionItemStatus = z.infer<typeof PickSessionItemStatusSchema>;
export interface IPickSessionItemStatus
  extends z.ZodType<PickSessionItemStatus> {}

export const HoldsPlacedByInfoSchema = z.object({
  source: createEnum(PlacedOnHoldSource, {
    enumName: "PlacedOnHoldSource",
  }).nullish(),
  placedByUserId: zExt.objectId().nullish(),
  placedByAutomationId: zExt.objectId().nullish(),
  placedByAutomationName: z.string().nullish(),
  releasedByUserId: zExt.objectId().nullish(),
});

export type HoldsPlacedByInfo = z.infer<typeof HoldsPlacedByInfoSchema>;

const fulfillmentGroupSchema_ = z
  .object({
    _id: zExt.objectId(),
    numericId: z.number().nullish(), // TODO @samuel: make non-nullish once all groups have a numericId and new groups are created with a numericId
    team: zExt.objectId(),
    teamName: z.string(),
    assignedFulfillmentService: zExt.objectId(),
    summary: FulfillmentGroupSummarySchema as IFulfillmentGroupSummary,
    slaFulfillmentDate: z.date().nullish(),
    batchId: z.string().nullish(),
    prettyBatchId: z.string().nullish(),
    voidedBatchId: z.string().nullish(),
    status: createEnum(FulfillmentGroupStatus, {
      enumName: "FulfillmentGroupStatus",
    }),
    fulfillmentOrders: z.array(FulfillmentOrderSchema as IFulfillmentOrder),
    shipmentRates: ShipmentRatesSchema.nullish(),
    // We have three weights that matter: totalWeightWithParcel, overrideWeight, and displayWeight
    // Total weight with parcel is the weight of the items plus the parcel, calculated on creation/update. We keep this in the case of "reset weight" in the UI
    // Override weight is only set when the user manually overrides the weight. It may be null
    // Display weight is the "summary" weight (not actually in the summary, but should be). It's the override weight if it exists, or the total weight with parcel if it doesn't. It's what we show to the user and search/filter on
    /** @deprecated Use selectedParcels instead */
    overrideWeight: WeightSchema.nullish(),
    displayWeightGrams: z.number().nullish(),
    /** @deprecated Use selectedParcels instead */
    selectedParcel: OutboundParcelSchema.nullish(),
    // Must be nullish before the migration. The transform will make other things infer non-nullish.
    selectedParcels: z.array(OutboundParcelSchema).nullish(),
    // selectedParcels: z.array(ParcelSchema).refine((parcels) => parcels.length > 0, {
    //   message: "At least one parcel is required",
    // }),
    selectedShippingRate: GroupSelectedRateSchema.nullish(),
    selectedShippingRateSetBy: createEnum(SelectedShippingRateSetBy, {
      enumName: "SelectedShippingRateSetBy",
    }).nullish(),
    selectedShippingRateSetByInfo:
      SelectedShippingRateSetByInfoSchema.nullish(),
    rateSelectionCriteria: RateSelectionCriteria.nullish(),
    createdAt: z.date(),
    updatedAt: z.date(),
    tags: z.array(TagSchema).nullish(),
    noteToBuyer: z.string().nullish(),
    noteFromBuyer: z.string().nullish(),
    internalNote: z.string().nullish(),
    assignedUserId: z.string().nullish(),
    // Not meaningful yet, needs a backfill
    shipmentDetails: ShipmentDetailsSchema.nullish(),
    // Shipment and additionalShipments will be deprecated soon, once we backfill shipments
    shipment: (ShipmentPackageSchema as IShipmentPackage).nullish(),
    additionalShipments: z
      .array(ShipmentPackageSchema as IShipmentPackage)
      .nullish(),
    fulfillments: z.array(RedoFulfillmentSchema as IRedoFulfillment).nullish(),
    printStatus: PrintStatusSchema.nullish(),
    learnedPresets: z.array(LearnedPresetSchema).nullish(),
    ratesPendingSince: z.date().nullish(),
    signatureRequired: z.boolean().nullish(),
    carbonNeutral: z.boolean().nullish(),
    canadaPostOption: createEnum(CanadaPostOption, {
      enumName: "CanadaPostOption",
    }).nullish(),
    // Required by UPS - Prevents rerouting of a package to an alternative address such as an Access Point
    directDeliveryOnly: z.boolean().nullish(),
    // Override the residential flag on the destination address
    overrideResidentialAddress: z.boolean().nullish(),
    adultSignatureRequired: z.boolean().nullish(),
    /** @deprecated Stored at the parcel level instead */
    includeInsurance: z.boolean().nullish(), // if this is true but insuredAmount is not set, we insure the whole package
    /** @deprecated Stored at the parcel level instead */
    insuredAmount: z.number().nullish(),
    specialRateEligibility: createEnum(SpecialRateEligibility, {
      enumName: "SpecialRateEligibility",
    }).nullish(),
    overrideDestinationAddresses: (
      FulfillmentGroupAddressesSchema as IFulfillmentGroupAddresses
    ).nullish(),
    overrideOriginAddress: z
      .object({
        addresses: (
          FulfillmentGroupAddressesSchema as IFulfillmentGroupAddresses
        ).nullish(),
        locationId: zExt.objectId().nullish(),
      })
      .nullish(),
    overrideDeliveryMethod: createEnum(RedoShopifyDeliveryMethodType, {
      enumName: "RedoShopifyDeliveryMethodType",
    }).nullish(),
    /** @deprecated Use selectedParcels instead */
    customs: FulfillmentGroupCustomsSchema.nullish(),
    /** @deprecated Use selectedParcels instead */
    contentsType: createEnum(ContentsType, {
      enumName: "ContentsType",
    }).nullish(),
    /** @deprecated Use selectedParcels instead */
    contentsExplanation: z.string().nullish(),
    /** @deprecated Use selectedParcels instead */
    additionalCustomsComments: z.string().nullish(),
    /** @deprecated Use selectedParcels instead */
    nonDeliveryOption: createEnum(NonDeliveryOption, {
      enumName: "NonDeliveryOption",
    }).nullish(),
    /** @deprecated Use selectedParcels instead */
    uspsExemptionCode: createEnum(USPSExemptionCode, {
      enumName: "USPSExemptionCode",
    }).nullish(),
    /** @deprecated Use selectedParcels instead */
    itn: z.string().nullish(),
    /** @deprecated Use selectedParcels instead */
    taxIdentifierIds: z.array(z.string()).nullish(),
    mergedTo: zExt.objectId().nullish(),
    ratesErrorMessage: z.string().nullish(),
    mergeSuggestionId: z.string().nullish(), // was going to be an objectId, but you cannot use objectIds for atlas search facets :(
    suggestionRejected: z.boolean().nullish(),
    disableAutoMerge: z.boolean().nullish(),
    pointerToNextGroup: zExt.objectId().nullish(), // If voided, we maintain a pointer to the group that was created as a result of the void
    scanVerified: z
      .object({
        verifiedBy: OMSUserSchema.nullish(),
        verifiedAt: z.date().nullish(),
      })
      .nullish(),
    scanVerifiedItemized: z
      .record(
        z.string(),
        z.array(
          z.object({ verifiedBy: z.string(), quantityVerified: z.number() }),
        ),
      )
      .nullish(),
    packingSlipTemplateId: zExt.objectId().nullish(), // Custom packing slip template ID for this fulfillment group
    personalizedSlipTemplate: z
      .object({
        templateId: zExt.objectId().nullish(),
        generatedUrl: z.string().nullish(), // URL of the generated personalized slip PDF
      })
      .nullish(),
    /** Product recommendations generated by the recommendations engine for personalized slips */
    productRecommendations: z
      .object({
        productIds: z.array(z.string()),
        source: createEnum(ProductRecommendationsSource, {
          enumName: "ProductRecommendationsSource",
        }).nullish(),
        generatedAt: z.date(),
      })
      .nullish(),
    holds: z.array(FulfillmentHoldSchema).nullish(),
    releaseHoldsAt: z.string().nullish(),
    holdsPlacedByInfo: HoldsPlacedByInfoSchema.nullish(),
    duplicateOf: zExt.objectId().nullish(),
    autoMergeRanAt: z.date().nullish(),
    autoMergeCooldown: z.date().nullish(),
    autoMergeLockedAt: z.date().nullish(),
    events: zExt.filterToValidArray(OMSEventSchema).nullish(),
    marketplaceNotification: MarketplaceNotificationStatusSchema.nullish(),
    ddp: z.boolean().nullish(),
    // We expect at most one landed cost per fulfillment group today, but use an
    // array to accommodate potential future multi-provider or multi-parcel scenarios.
    landedCosts: z.array(LockedLandedCostSchema).nullish(),
    fulfilledBy: createEnum(FulfilledBy, { enumName: "FulfilledBy" }).nullish(),
    hazmat: createEnum(Hazmat, { enumName: "Hazmat" }).nullish(),
    rateLearningOverwritten: z.boolean().nullish(),
    parcelLearningOverwritten: z.boolean().nullish(),
    weightLearningOverwritten: z.boolean().nullish(),
    overrideReturnAddress: FulfillmentOrderAddressSchema.nullish(),
    saturdayDelivery: z.boolean().nullish(),
    assignmentAutomationRanAt: z.date().nullish(),
    archivedInterallyAt: z.date().nullish(),
    recordedShipment: RecordedShipmentSchema.nullish(),
    integrationReferences: z
      .array(IntegrationReferenceSchema as IIntegrationReference)
      .nullish(),
    customerAccountsToGetRatesFor: z.array(z.string()).nullish(), // unused now, only present for backwards compatibility
    manualFulfillmentInfo: FulfillmentInfoSchema.nullish(),
    /** TODO: Remove this when IMS is source of truth for comment sold RMR inventory adjustment tracking */
    csInventoryIncrementedLineItemIds: z.array(z.string()).nullish(),
    /** @deprecated Use shippingClaimIdsByTrackingNumber instead */
    shippingClaimIds: z.array(z.string()).nullish(),
    shippingClaimIdsByTrackingNumber: z
      .record(z.string(), z.array(z.string()))
      .nullish(),
    needsRatePreparation: z.boolean().nullish(),
    dryIceWeight: WeightSchema.nullish(),
    notifyMarketplace: z.boolean().nullish(),
    notifyCustomer: z.boolean().nullish(),
    fulfillmentDate: z.date().nullish(),
    buyReturnLabel: z.boolean().nullish(),
    /** @deprecated Use overriddenAsReceivedFulfillmentOrderIds instead */
    overrideAsReceived: z.boolean().nullish(),
    overriddenAsReceivedFulfillmentOrderIds: z.array(z.string()).nullish(),
    pickSessionId: z.string().nullish(),
    pickStartedOn: z.date().nullish(),
    assignedPickerId: z.string().nullish(),
    pickStatus: createEnum(FulfillmentGroupPickStatus, {
      enumName: "FulfillmentGroupPickStatus",
    }).nullish(),
    pickedAt: z.date().nullish(),
    problemResolvedAt: z.date().nullish(),
    // When an order is "problem", and this is not present, we assume all items are unpicked
    pickSessionItemStatuses: z
      .array(PickSessionItemStatusSchema as IPickSessionItemStatus)
      .nullish(),
    rerunningAutomationsAt: z.date().nullish(),
    failedShipments: z
      .array(ShipmentPackageSchema as IShipmentPackage)
      .nullish(),
    returnShipment: ShipmentPackageSchema.extend({
      // used for queries in the cron job - tracking events are the source of truth
      // about scan and balance transactions are the source of truth about charge
      status: createEnum(ReturnShipmentStatus, {
        enumName: "ReturnShipmentStatus",
      }).nullish(),
    }).nullish(),
    isSplit: z.boolean().nullish(),
    shipmentPaymentOption: ShipmentPaymentOptionSchema.nullish(),
    // We will eventually move towards shipmentPaymentOptions being the source of truth for payment options - but right now we just use the single option above
    // We are writing to both so that the migration will be trivial
    shipmentPaymentOptions: z.array(ShipmentPaymentOptionSchema).nullish(),
    blockedRateProviders: z
      .array(createEnum(RateProvider, { enumName: "RateProvider" }))
      .nullish(),
    // Even if autoCleanAddress setting is true, if this is true, we will use the original destination address instead of the verified destination address
    overrideUseOriginalDestinationAddress: z.boolean().nullish(),
    addressUpdatesMetadata: z
      .object({
        addressFixedBy: createEnum(AddressFixedBy, {
          enumName: "AddressFixedBy",
        }).nullish(),
        addressFixedAt: z.date().nullish(),
      })
      .nullish(),
    trackingSyncedAt: z.date().nullish(),
    // A unique index exists on this field (excluding null/undefined) so that we can ensure that we have idempotency when performing operations that create new fulfillment groups in temporal (like moving/voiding fulfillment groups)
    uniqueIdentifier: z.string().nullish(),
    returnShipmentOverrides: z
      .object({
        shopperAddress: FulfillmentOrderAddressSchema.nullish(),
        merchantAddress: FulfillmentOrderAddressSchema.nullish(),
        parcel: OutboundParcelSchema.nullish(),
      })
      .nullish(),
    buyLabelErrors: z.array(LabelErrorSchema).nullish(),
    buyReturnLabelErrors: z.array(LabelErrorSchema).nullish(),
    forceResyncAt: z.date().nullish(),
    packingParcels: PackingParcelsSchema.nullish(),
    itemParcelAssignments: ItemParcelAssignmentsSchema.nullish(),
    parcelsFinalized: z.boolean().nullish(),
    papsNumber: z.string().nullish(),
    portOfEntry: z.string().nullish(),
    crossingDate: z.date().nullish(),
    // When a fulfillment group is cancelled in Redo without restocking inventory,
    // this flag is set so IMS can deduct the items from on-hand quantity.
    cancelledWithoutRestock: z.boolean().nullish(),
    /**
     * Tracks IDs used to ensure idempotent-like behavior across different runs
     * of OMS automation flows.
     */
    idempotencyControl: z
      .object({
        lastSetParcelActionFulfillmentGroupAutomationsRunId: z
          .string()
          .nullish(),
      })
      .nullish(),
  })
  .transform(({ selectedParcels, ...rest }) => {
    const selectedParcelsWithOverrideWeight =
      selectedParcels ??
      (rest.selectedParcel
        ? [rest.selectedParcel]
        : [
            {
              ...DEFAULT_REDO_PARCEL,
              parcelAndContentWeight: null,
              insurance: null,
              customs: null,
              dryIceWeight: null,
              hazmat: null,
            },
          ]);

    if (selectedParcelsWithOverrideWeight[0]) {
      selectedParcelsWithOverrideWeight[0] = {
        ...selectedParcelsWithOverrideWeight[0],
        parcelAndContentWeight:
          selectedParcelsWithOverrideWeight[0]?.parcelAndContentWeight ??
          rest.overrideWeight,
        customs: {
          itn: rest.itn,
          uspsExemptionCode: rest.uspsExemptionCode,
          nonDeliveryOption: rest.nonDeliveryOption,
          contentsType: rest.contentsType,
          contentsExplanation: rest.contentsExplanation,
          additionalCustomsComments: rest.additionalCustomsComments,
          taxIdentifierIds: rest.taxIdentifierIds,
          declarations: rest.customs?.declarations,
          ...selectedParcelsWithOverrideWeight[0]?.customs,
        },
        insurance: {
          includeInsurance: rest.includeInsurance,
          insuredAmount: rest.insuredAmount,
          ...selectedParcelsWithOverrideWeight[0]?.insurance,
        },
        hazmat: selectedParcelsWithOverrideWeight[0]?.hazmat ?? rest.hazmat,
        dryIceWeight:
          selectedParcelsWithOverrideWeight[0]?.dryIceWeight ??
          rest.dryIceWeight,
      };
    }

    if (rest.shipment) {
      rest.shipment.recordedShipment ??= rest.recordedShipment;
    }

    if (rest.shipmentRates?.rates?.length) {
      rest.shipmentRates.rates = rest.shipmentRates.rates.map(
        ensureCarrierAccountId,
      );
    }

    return { ...rest, selectedParcels: selectedParcelsWithOverrideWeight };
  });

export const FulfillmentGroupSchema: z.ZodType<IFulfillmentGroup> =
  fulfillmentGroupSchema_;

export interface IFulfillmentGroup
  extends z.infer<typeof fulfillmentGroupSchema_> {}
export type FulfillmentGroup = z.infer<typeof fulfillmentGroupSchema_>;

export const fulfillmentServiceSchema = z.object({
  _id: zExt.objectId(),
  name: z.string(),
});
export type FulfillmentService = z.infer<typeof fulfillmentServiceSchema>;
export interface IFulfillmentService extends z.ZodType<FulfillmentService> {}

export const ProviderActionMessagesSchema = z.object({
  errorMessage: z.string().nullable(),
  warningMessage: z.string().nullable(),
});
export type ProviderActionMessages = z.infer<
  typeof ProviderActionMessagesSchema
>;

const fulfillmentGroupDataSchema_ = FulfillmentGroupSummarySchema.extend({
  _id: z.string(),
  tags: z.array(TagSchema).nullish(),
  releaseHoldsAt: z.string().nullish(),
  orders: z.array(
    z.object({
      redoOrderId: z.string().nullish(),
      name: z.string(),
      secondaryOrderName: z.string().nullish(),
      tags: z.array(z.string()),
      id: z.string(),
      packageProtected: z.boolean().nullish(),
      fulfillmentOrderExternalId: z.string(),
      customAttributes: z
        .array(z.object({ key: z.string(), value: z.string().nullish() }))
        .nullish(),
    }),
  ),
  /**
   * @deprecated Use fulfillmentOrderToDeliveryMethod instead
   */
  shippingMethodNames: z.array(z.string()),
  fulfillmentOrderToDeliveryMethod: z.record(
    z.string(),
    z.object({
      name: z.string(),
      code: z.string().nullish(),
      kind: createEnum(RedoShopifyDeliveryMethodType, {
        enumName: "RedoShopifyDeliveryMethodType",
      }),
    }),
  ),
  batchId: z.string().nullish(),
  prettyBatchId: z.string().nullish(),
  /**
   * WARNING: Line items may contain sub items from Bill of Materials (BOM) expansion.
   * If the use case is related to picking items (packing slips, pick lists, scanning, pick sessions, etc) - you probably want to use `expandLineItemsToPickableItems(items)` to get only the leaf node items.
   * In most cases (splitting, table filtering, etc) - you probably want to use the top level line items - the actual line items that were ordered.
   */
  items: z.array(
    FulfillmentOrderLineItemSchema_.extend({
      fulfillmentOrderExternalId: z.string(),
    }),
  ),
  insuranceProvider: createEnum(OmsInsuranceProvider, {
    enumName: "OmsInsuranceProvider",
  }).nullish(),
  originAddress: FulfillmentOrderAddressSchema as IFulfillmentOrderAddress,
  verifiedOriginAddress: (VerifiedAddressSchema as IVerifiedAddress).nullish(),
  destinationAddress: FulfillmentOrderAddressSchema as IFulfillmentOrderAddress,
  verifiedDestinationAddress: (
    VerifiedAddressSchema as IVerifiedAddress
  ).nullish(),
  billingAddress: (
    FulfillmentOrderAddressSchema as IFulfillmentOrderAddress
  ).nullish(),
  status: createEnum(FulfillmentGroupStatus, {
    enumName: "FulfillmentGroupStatus",
  }),
  rerunningAutomationsAt: z.date().nullish(),
  selectedParcels: z.array(OutboundParcelSchema).nullish(),
  customerShippingPaid: z.string().nullish(),
  availableShipmentRates: ShipmentRatesSchema.nullish(),
  selectedShippingRate: GroupSelectedRateSchema.nullish(),
  displayWeightGrams: z.number().nullish(),
  assignedUser: (OMSUserSchema as IOMSUser).nullish(),
  // Not meaningful yet, needs a backfill
  shipmentDetails: ShipmentDetailsSchema.nullish(),
  // Shipment and additionalShipments will be deprecated soon, once we backfill shipments
  shipment: (ShipmentPackageSchema as IShipmentPackage).nullish(),
  // These are shipments from multi-parcel orders that do not have the master tracker
  additionalShipments: z
    .array(ShipmentPackageSchema as IShipmentPackage)
    .nullish(),
  returnShipment: ShipmentPackageSchema.nullish(),
  noteToBuyer: z.string().nullish(),
  noteFromBuyer: z.string().nullish(),
  internalNote: z.string().nullish(),
  marketplaceInternalNote: z.string().nullish(),
  customAttributes: z.array(
    z.object({ key: z.string(), value: z.string().nullish() }),
  ),
  customerName: z.string().nullish(),
  customerEmail: z.string().nullish(),
  printStatus: PrintStatusSchema.nullish(),
  orderSources: z.array(createEnum(Provider, { enumName: "Provider" })),
  risk: (RiskSchema as IRisk).nullish(),
  ratesPendingSince: z.date().nullish(),
  signatureRequired: z.boolean().nullish(),
  adultSignatureRequired: z.boolean().nullish(),
  /** @deprecated Stored at the parcel level instead */
  includeInsurance: z.boolean().nullish(), // if this is true but insuredAmount is not set, we insure the whole package
  /** @deprecated Stored at the parcel level instead */
  insuredAmount: z.number().nullish(),
  specialRateEligibility: createEnum(SpecialRateEligibility, {
    enumName: "SpecialRateEligibility",
  }).nullish(),
  ratesErrorMessage: z.string().nullish(),
  mergedTo: zExt.objectId().nullish(),
  pointerToNextGroup: zExt.objectId().nullish(), // If voided, we maintain a pointer to the group that was created as a result of the void
  /** @deprecated Not used anymore */
  insured: z.boolean().nullish(),
  fulfilledBy: createEnum(FulfilledBy, { enumName: "FulfilledBy" }).nullish(),
  // Stores is really sales channels - we will remove this once everything is deployed
  stores: z.array(z.string()),
  salesChannels: z.array(z.string()).nullish(),
  originStore: z.string().nullish(),
  scanVerified: z
    .object({
      verifiedBy: (OMSUserSchema as IOMSUser).nullish(),
      verifiedAt: z.date().nullish(),
    })
    .nullish(),
  packingSlipTemplateId: z.string().nullish(),
  fulfillmentOrderIds: z.array(z.string()),
  events: zExt.filterToValidArray(OMSEventSchema as IOMSEvent).nullish(),
  /** @deprecated Stored at the parcel level instead */
  customs: OutboundParcelCustomsSchema.nullish(),
  marketplaceNotification: (
    MarketplaceNotificationStatusSchema as IMarketplaceNotificationStatus
  ).nullish(),
  locationsForMove: z.array(LocationForMoveSchema).nullish(),
  selectedLocationId: z.string().nullable(),
  contentsType: createEnum(ContentsType, {
    enumName: "ContentsType",
  }).nullish(),
  contentsExplanation: z.string().nullish(),
  nonDeliveryOption: createEnum(NonDeliveryOption, {
    enumName: "NonDeliveryOption",
  }).nullish(),
  uspsExemptionCode: createEnum(USPSExemptionCode, {
    enumName: "USPSExemptionCode",
  }).nullish(),
  itn: z.string().nullish(),
  taxIdentifierIds: z.array(z.string()).nullish(),
  ddp: z.boolean().nullish(),
  duplicateOf: zExt.objectId().nullish(),
  hazmat: createEnum(Hazmat, { enumName: "Hazmat" }).nullish(),
  providers: z.array(createEnum(Provider, { enumName: "Provider" })).nullish(),
  usedLearning: z
    .record(
      createEnum(LearnedPresetKind, { enumName: "LearnedPresetKind" }),
      z.boolean(),
    )
    .nullish(),
  overrideReturnAddress: FulfillmentOrderAddressSchema.nullish(),
  saturdayDelivery: z.boolean().nullish(),
  canPlaceHold: z.boolean().nullish(),
  assignedFulfillmentService: (
    fulfillmentServiceSchema as IFulfillmentService
  ).nullish(),
  teamInfo: z.object({ _id: zExt.objectId(), name: z.string() }).nullish(),
  /** @deprecated Use shippingClaimIdsByTrackingNumber instead */
  shippingClaimIds: z.array(z.string()).nullish(),
  shippingClaimIdsByTrackingNumber: z
    .record(z.string(), z.array(z.string()))
    .nullish(),
  dryIceWeight: WeightSchema.nullish(),
  providerActionMessages: ProviderActionMessagesSchema.nullish(),
  supportedActions: z.array(
    createEnum(RedoShopifyFulfillmentOrderAction, {
      enumName: "RedoShopifyFulfillmentOrderAction",
    }),
  ),
  buyReturnLabel: z.boolean().nullish(),
  isRefunded: z.boolean().nullish(),
  needsRatePreparation: z.boolean().nullish(),
  shipmentPaymentOption: (
    ShipmentPaymentOptionSchema as IShipmentPaymentOption
  ).nullish(),
  customDate1: z.array(z.date()).nullish(),
  customDate2: z.array(z.date()).nullish(),
  customDate3: z.array(z.date()).nullish(),
  customString1: z.array(z.string()).nullish(),
  customString2: z.array(z.string()).nullish(),
  customString3: z.array(z.string()).nullish(),
  customNumber1: z.array(z.number()).nullish(),
  customNumber2: z.array(z.number()).nullish(),
  customNumber3: z.array(z.number()).nullish(),
  customAttributeDate1: z.array(z.date()).nullish(),
  customAttributeDate2: z.array(z.date()).nullish(),
  customAttributeDate3: z.array(z.date()).nullish(),
  customAttributeString1: z.array(z.string()).nullish(),
  customAttributeString2: z.array(z.string()).nullish(),
  customAttributeString3: z.array(z.string()).nullish(),
  customAttributeNumber1: z.array(z.number()).nullish(),
  customAttributeNumber2: z.array(z.number()).nullish(),
  customAttributeNumber3: z.array(z.number()).nullish(),
  uniqueProductsCount: z.number().nullish(),
  slaFulfillmentDate: z.date().nullish(),
  carbonNeutral: z.boolean().nullish(),
  canadaPostOption: createEnum(CanadaPostOption, {
    enumName: "CanadaPostOption",
  }).nullish(),
  directDeliveryOnly: z.boolean().nullish(),
  overrideResidentialAddress: z.boolean().nullish(),
  additionalCustomsComments: z.string().nullish(),
  personalizedSlipTemplateId: z.string().nullish(),
  overrideUseOriginalDestinationAddress: z.boolean().nullish(),
  shipmentMessages: z
    .record(z.string(), z.array(ShipmentMessageSchema))
    .nullish(),
  paymentGateways: z.array(z.string()).nullish(),
  buyLabelErrors: z.array(LabelErrorSchema).nullish(),
  buyReturnLabelErrors: z.array(LabelErrorSchema).nullish(),
  pickSessionId: z.string().nullish(),
  pickStartedOn: z.date().nullish(),
  assignedPicker: (OMSUserSchema as IOMSUser).nullish(),
  pickStatus: createEnum(FulfillmentGroupPickStatus, {
    enumName: "FulfillmentGroupPickStatus",
  }).nullish(),
  pickedAt: z.date().nullish(),
  problemResolvedAt: z.date().nullish(),
  // When an order is "problem", and this is not present, we assume all items are unpicked
  pickSessionItemStatuses: z
    .array(PickSessionItemStatusSchema as IPickSessionItemStatus)
    .nullish(),
  assignedTotes: z.array(RedoFulfillmentToteSchema).nullish(),
  selectedShippingRateSetByInfo: SelectedShippingRateSetByInfoSchema.nullish(),
  selectedShippingRateSetBy: createEnum(SelectedShippingRateSetBy, {
    enumName: "SelectedShippingRateSetBy",
  }).nullish(),
  holdsPlacedByInfo: HoldsPlacedByInfoSchema.nullish(),
  holds: z.array(FulfillmentHoldSchema).nullish(),
  scanVerifiedItemized: z
    .record(
      z.string(),
      z.array(
        z.object({ verifiedBy: z.string(), quantityVerified: z.number() }),
      ),
    )
    .nullish(),
  packingParcels: PackingParcelsSchema.nullish(),
  itemParcelAssignments: ItemParcelAssignmentsSchema.nullish(),
  parcelsFinalized: z.boolean().nullish(),
  hasLandedCost: z.boolean().nullish(),
  lineItemPickability: z
    .array(
      z.object({
        id: z.string(),
        title: z.string(),
        productTitle: z.string(),
        sku: z.string().nullish(),
        barcode: z.string().nullish(),
        quantity: z.number(),
        quantityAvailable: z.number(),
        isPickable: z.boolean(),
        imageUrl: z.string().nullish(),
        bins: z
          .array(
            z.object({
              storageLocationId: z.string(),
              storageLocationName: z.string(),
              quantity: z.number(),
            }),
          )
          .nullish(),
      }),
    )
    .nullish(),
  pickabilityStatus: z
    .enum(["all_pickable", "partially_pickable", "not_pickable"])
    .nullish(),
});

export const FulfillmentGroupDataSchema: z.ZodType<IFulfillmentGroupData> =
  fulfillmentGroupDataSchema_;

export interface IFulfillmentGroupData
  extends z.infer<typeof fulfillmentGroupDataSchema_> {}
export type FulfillmentGroupData = IFulfillmentGroupData;

export const FulfillmentGroupDataAsyncInfoSchema = z.object({
  locations: z.array(RedoFulfillmentLocationSchema),
  fulfillmentServices: z.array(fulfillmentServiceSchema),
  degroupByCustomAttributes: z.boolean().nullish(),
  teamUsers: z.array(OMSUserSchema),
  teamInfo: fulfillmentServiceSchema.nullable(),
});
export type FulfillmentGroupDataAsyncInfo = z.infer<
  typeof FulfillmentGroupDataAsyncInfoSchema
>;

export function sortFulfillmentOrderLineItems(
  orderBy: ItemOrderBy,
  items: FulfillmentOrderLineItem[],
): FulfillmentOrderLineItem[] {
  switch (orderBy) {
    case ItemOrderBy.QuantityDesc:
      return items.sort((a, b) => b.quantity - a.quantity);
    case ItemOrderBy.QuantityAsc:
      return items.sort((a, b) => a.quantity - b.quantity);
    case ItemOrderBy.NameAsc:
      return items.sort((a, b) => a.title.trim().localeCompare(b.title.trim()));
    case ItemOrderBy.NameDesc:
      return items.sort((a, b) => b.title.trim().localeCompare(a.title.trim()));
    case ItemOrderBy.SkuAsc:
      return items.sort((a, b) =>
        (a.sku || "").trim().localeCompare((b.sku || "").trim()),
      );
    case ItemOrderBy.SkuDesc:
      return items.sort((a, b) =>
        (b.sku || "").trim().localeCompare((a.sku || "").trim()),
      );
    case ItemOrderBy.TotalPriceAsc:
      return items.sort(
        (a, b) => Number(a.totalPrice.amount) - Number(b.totalPrice.amount),
      );
    case ItemOrderBy.TotalPriceDesc:
      return items.sort(
        (a, b) => Number(b.totalPrice.amount) - Number(a.totalPrice.amount),
      );
    case ItemOrderBy.UnitPriceAsc:
      return items.sort(
        (a, b) => Number(a.unitPrice.amount) - Number(b.unitPrice.amount),
      );
    case ItemOrderBy.UnitPriceDesc:
      return items.sort(
        (a, b) => Number(b.unitPrice.amount) - Number(a.unitPrice.amount),
      );
    case ItemOrderBy.BinAsc:
      return items.sort((a, b) =>
        (a.bin || "")
          .trim()
          .localeCompare((b.bin || "").trim(), undefined, { numeric: true }),
      );
    case ItemOrderBy.BinDesc:
      return items.sort((a, b) =>
        (b.bin || "")
          .trim()
          .localeCompare((a.bin || "").trim(), undefined, { numeric: true }),
      );
    case ItemOrderBy.OrdersAsc:
      return items.sort((a, b) =>
        (a.orderId || "")
          .trim()
          .localeCompare((b.orderId || "").trim(), undefined, {
            numeric: true,
          }),
      );
    case ItemOrderBy.OrdersDesc:
      return items.sort((a, b) =>
        (b.orderId || "")
          .trim()
          .localeCompare((a.orderId || "").trim(), undefined, {
            numeric: true,
          }),
      );
    case ItemOrderBy.VendorAsc:
      return items.sort((a, b) =>
        (a.vendor || "").trim().localeCompare((b.vendor || "").trim()),
      );
    case ItemOrderBy.VendorDesc:
      return items.sort((a, b) =>
        (b.vendor || "").trim().localeCompare((a.vendor || "").trim()),
      );
  }
}

export function getWeightWithParcelsGrams(
  totalWeightGrams: number,
  parcels: Parcel[] | null | undefined,
): Weight {
  const orderWeight: Weight = {
    unit: WeightUnit.GRAM,
    value: totalWeightGrams,
  };

  if (!parcels) {
    return orderWeight;
  }

  const parcelWeights = parcels.map((p) => getParcelWeightGrams(p));
  const totalWeight = parcelWeights.reduce((acc, parcelWeight) => {
    return addWeight(acc, parcelWeight);
  }, orderWeight);

  return convertWeight(totalWeight, WeightUnit.GRAM);
}

export function getDisplayWeightGrams(
  overrideWeight: Weight | null | undefined,
  totalWeightWithParcelGrams: number,
): number {
  return overrideWeight
    ? convertWeight(overrideWeight, WeightUnit.GRAM).value
    : totalWeightWithParcelGrams;
}

export interface FulfillmentGroupForPackingSlip {
  fulfillmentGroupId: string;
  recipientFirstName: string;
  recipientFullName: string;
  orderItems: OrderItemTableColumnData[];
  destinationAddress: ExtendedAddress;
  originAddress: ExtendedAddress;
  billingAddress: ExtendedAddress;
  trackingNumber: string | null;
  recipientPhone: string;
  recipientEmail: string;
  orderNumber: string;
  orderNumberWithoutMarketplaceIdentifier: string;
  secondaryOrderName: string;
  orderDate: string;
  printedDate: string;
  packageWeight: { weight: number; unit: string };
  packageInformation: { name: string; dimensions: string[] }[];
  productsPrice: string;
  shippingPrice: string;
  tax: string;
  orderTotal: string;
  totalDiscountedPrice: string;
  totalDiscounts: string | undefined;
  discountCodes: string[] | undefined;
  combinedDiscountCodes: string;
  paymentMethod: string | undefined;
  lastFourCardNumber: string | undefined;
  shippingCarrier: string;
  shippingService: string;
  requestedService: string;
  noteToCustomer: string;
  noteFromCustomer: string;
  salesChannel: string;
  totalQuantity: number;
  orderForReturnsPortal: FulfillmentOrder | undefined;
  fulfilledFromMultipleLocationsText: string;
  orderTags: string;
  customerId: string;
  customerNumOrders: string;
  customDate1: string;
  customDate2: string;
  customDate3: string;
  customString1: string;
  customString2: string;
  customString3: string;
  customNumber1: string;
  customNumber2: string;
  customNumber3: string;
  customAttributeDate1: string;
  customAttributeDate2: string;
  customAttributeDate3: string;
  customAttributeString1: string;
  customAttributeString2: string;
  customAttributeString3: string;
  customAttributeNumber1: string;
  customAttributeNumber2: string;
  customAttributeNumber3: string;
}

export function getSelectedRate({
  selectedShippingRate,
  availableRates,
}: {
  selectedShippingRate: SelectedShippingRate | null | undefined;
  availableRates: OutboundRate[] | null | undefined;
}): OutboundRate | undefined {
  if (!selectedShippingRate || !availableRates) {
    return undefined;
  }

  return availableRates.find((rate) => {
    let condition =
      carrierToLabel[rate.carrier] ===
        carrierToLabel[selectedShippingRate.carrier] &&
      servicesMatch(rate.service, selectedShippingRate.service);

    if (selectedShippingRate.carrier_account_id) {
      condition =
        condition &&
        rate.carrier_account_id === selectedShippingRate.carrier_account_id;
    }

    return condition;
  });
}

export function getFulfillmentOrdersByOrder(
  groups: FulfillmentGroup[],
): Record<string, FulfillmentOrder[]> {
  const byOrder = groups
    .flatMap((group) => group.fulfillmentOrders)
    .reduce(
      (acc, fulfillmentOrder) => {
        if (fulfillmentOrder.order) {
          const existing = acc[fulfillmentOrder.order.id] ?? [];
          existing.push(fulfillmentOrder);
          acc[fulfillmentOrder.order.id] = existing;
        }
        return acc;
      },
      {} as Record<string, FulfillmentOrder[]>,
    );
  return byOrder;
}

export function getFulfillmentGroupsOrderIds(
  groups: FulfillmentGroup[],
): string[] {
  const allOrders = groups
    .flatMap((group) => group.fulfillmentOrders.map((fo) => fo.order?.id))
    .filter((id): id is string => id !== undefined);
  const uniqueOrders = new Set(allOrders);
  return Array.from(uniqueOrders);
}

/**
 * Gets the total quantity of items to scan/verify.
 * Uses expandedItemQuantity if available, otherwise falls back to totalItemQuantity.
 */
export function getScanItemQuantity(
  fulfillmentGroup: FulfillmentGroupData | null | undefined,
): number {
  if (!fulfillmentGroup) {
    return 0;
  }
  return (
    fulfillmentGroup.expandedItemQuantity ?? fulfillmentGroup.totalItemQuantity
  );
}

/**
 * Checks if the fulfillment group has any BOM-expanded items.
 * Returns true if any line item has billOfMaterialsInfos with expansion items.
 */
export function hasBomExpansion(
  fulfillmentGroup: FulfillmentGroupData,
): boolean {
  return fulfillmentGroup.items.some((item) =>
    item.billOfMaterialsInfos?.some((info) => info.expansionItems.length > 0),
  );
}

export function getFulfillmentGroupValue(
  fulfillmentGroup: FulfillmentGroup | FulfillmentGroupData,
): number {
  if ("summary" in fulfillmentGroup) {
    // Calculate total including shipping and taxes (not just subtotal)
    const subtotal =
      fulfillmentGroup.summary.totalDiscountedPrice !== undefined &&
      fulfillmentGroup.summary.totalDiscountedPrice !== null
        ? Number(fulfillmentGroup.summary.totalDiscountedPrice.amount)
        : Number(fulfillmentGroup.summary.totalPrice.amount);
    const shippingCost = Number(
      fulfillmentGroup.summary.totalShippingCost.amount,
    );
    const taxes = Number(fulfillmentGroup.summary.totalTax.amount);
    return subtotal + shippingCost + taxes;
  } else {
    const subtotal =
      fulfillmentGroup.totalDiscountedPrice !== undefined &&
      fulfillmentGroup.totalDiscountedPrice !== null
        ? Number(fulfillmentGroup.totalDiscountedPrice.amount)
        : Number(fulfillmentGroup.totalPrice.amount);
    const shippingCost = Number(fulfillmentGroup.totalShippingCost.amount);
    const taxes = Number(fulfillmentGroup.totalTax.amount);
    return subtotal + shippingCost + taxes;
  }
}

export function findShipmentByTracker(
  fulfillmentGroup: FulfillmentGroup | FulfillmentGroupData,
  trackingNumber: string,
): ShipmentPackage | undefined | null {
  return [
    fulfillmentGroup.shipment,
    ...(fulfillmentGroup.additionalShipments ?? []),
  ].find((s) => s?.tracker?.trackingCode === trackingNumber);
}

export const knownRatesErrorMessages = [
  "No HS codes found for products",
  "The parameters passed contained an invalid character.",
  "Some SKUs may be truncated on the shipping label",
  "Origin address zip is required",
  "Origin address line 1 contains bad characters",
  "Destination address line 1 contains bad characters",
  "Destination address name contains bad characters",
  "Address line 1 may need to be fewer than 30 alphanumeric characters",
  "Contents explanation required - item descriptions will be used as a fallback",
];

export function uniqueOrderNames(
  fulfillmentGroup: FulfillmentGroup | FulfillmentGroupData,
) {
  if ("fulfillmentOrders" in fulfillmentGroup) {
    const orderNames = fulfillmentGroup.fulfillmentOrders
      .map((fo) => fo.order?.name)
      .filter((name) => name !== null && name !== undefined);
    return [...new Set(orderNames)];
  }
  return [...new Set(fulfillmentGroup.orders.map((o) => o.name))];
}

export function shouldUseVerifiedDestinationAddress(
  group: FulfillmentGroupData | FulfillmentGroup,
  autoCleanAddresses: boolean | undefined | null,
): boolean {
  if (group.overrideUseOriginalDestinationAddress) {
    return false;
  }
  return !!autoCleanAddresses;
}

export function getMergeHoldId(fulfillmentOrderId: string) {
  return "redo-merge-" + fulfillmentOrderId;
}

export const FulfillmentGroupKeys = Object.keys(
  FulfillmentGroupSchema,
) as (keyof FulfillmentGroup)[];

export function fulfillmentGroupExhaustiveCheck() {
  for (const k of FulfillmentGroupKeys) {
    switch (k) {
      case "_id":
      case "numericId":
      case "uniqueIdentifier":
      case "createdAt":
      case "updatedAt":
      case "team":
      case "teamName":
      case "assignedFulfillmentService":
      case "assignedUserId":
      case "fulfillmentOrders":
      case "summary":
      case "status":
      case "selectedParcels":
      case "selectedShippingRate":
      case "selectedParcel": // deprecated
      case "selectedShippingRateSetBy": // deprecated
      case "selectedShippingRateSetByInfo":
      case "rateSelectionCriteria":
      case "shipmentRates":
      case "ratesPendingSince":
      case "ratesErrorMessage":
      case "blockedRateProviders":
      case "overrideWeight": // deprecated
      case "displayWeightGrams":
      case "dryIceWeight":
      case "noteToBuyer":
      case "noteFromBuyer":
      case "internalNote":
      case "tags":
      case "shipment":
      case "additionalShipments":
      case "fulfillments":
      case "failedShipments":
      case "recordedShipment":
      case "printStatus":
      case "packingSlipTemplateId":
      case "personalizedSlipTemplate":
      case "learnedPresets":
      case "rateLearningOverwritten":
      case "parcelLearningOverwritten":
      case "weightLearningOverwritten":
      case "needsRatePreparation":
      case "assignmentAutomationRanAt":
      case "rerunningAutomationsAt":
      case "signatureRequired":
      case "carbonNeutral":
      case "directDeliveryOnly":
      case "adultSignatureRequired":
      case "saturdayDelivery":
      case "hazmat":
      case "ddp":
      case "includeInsurance":
      case "insuredAmount":
      case "specialRateEligibility":
      case "overrideDestinationAddresses":
      case "overrideOriginAddress":
      case "overrideReturnAddress":
      case "overrideResidentialAddress":
      case "overrideUseOriginalDestinationAddress":
      case "overrideDeliveryMethod":
      case "addressUpdatesMetadata":
      case "customs":
      case "contentsType":
      case "contentsExplanation":
      case "additionalCustomsComments":
      case "nonDeliveryOption":
      case "uspsExemptionCode":
      case "itn":
      case "taxIdentifierIds":
      case "mergedTo":
      case "pointerToNextGroup":
      case "duplicateOf":
      case "mergeSuggestionId":
      case "suggestionRejected":
      case "disableAutoMerge":
      case "autoMergeRanAt":
      case "autoMergeCooldown":
      case "autoMergeLockedAt":
      case "batchId":
      case "prettyBatchId":
      case "slaFulfillmentDate":
      case "scanVerified":
      case "holds":
      case "releaseHoldsAt":
      case "events":
      case "marketplaceNotification":
      case "notifyMarketplace":
      case "notifyCustomer":
      case "fulfillmentDate":
      case "fulfilledBy":
      case "manualFulfillmentInfo":
      case "pickSessionId":
      case "overrideAsReceived":
      case "archivedInterallyAt":
      case "trackingSyncedAt":
      case "forceResyncAt":
      case "integrationReferences":
      case "customerAccountsToGetRatesFor":
      case "shippingClaimIds":
      case "shippingClaimIdsByTrackingNumber":
      case "buyReturnLabel":
      case "returnShipment":
      case "returnShipmentOverrides":
      case "isSplit":
      case "shipmentPaymentOption":
      case "shipmentPaymentOptions":
      case "buyLabelErrors":
      case "buyReturnLabelErrors":
      case "shipmentDetails":
      case "pickStatus":
      case "pickSessionItemStatuses":
      case "pickedAt":
      case "problemResolvedAt":
      case "pickStartedOn":
      case "assignedPickerId":
      case "holdsPlacedByInfo":
      case "voidedBatchId":
      case "scanVerifiedItemized":
      case "productRecommendations":
      case "overriddenAsReceivedFulfillmentOrderIds":
      case "packingParcels":
      case "itemParcelAssignments":
      case "parcelsFinalized":
      case "landedCosts":
      case "papsNumber":
      case "portOfEntry":
      case "crossingDate":
      case "csInventoryIncrementedLineItemIds":
      case "cancelledWithoutRestock":
      case "idempotencyControl":
      case "canadaPostOption":
        break;
      // Every time we add a new key to a fulfillment group, we need to consider if the field should be copied to the new fulfillment group on split/void - this check makes us think about that
      default:
        assertNever(k);
    }
  }
}

export function getFulfillmentGroupItems(
  group: FulfillmentGroup | FulfillmentGroupData,
): FulfillmentOrderLineItem[] {
  if ("fulfillmentOrders" in group) {
    return group.fulfillmentOrders.flatMap((fo) => fo.lineItems ?? []);
  }
  return group.items ?? [];
}

// Returns null if there are no pick records on the fulfillment group
export function getNumUnpickedItems(
  group: FulfillmentGroup | FulfillmentGroupData,
): number | null {
  // Only calculate unpicked items for PROBLEM pickStatus
  if (group.pickStatus !== FulfillmentGroupPickStatus.PROBLEM) {
    return null;
  }

  const rawItems = getFulfillmentGroupItems(group);
  const pickableItems = expandLineItemsToPickableItems(rawItems);
  const totalQuantity = pickableItems.reduce((acc, item) => {
    return acc + item.quantity;
  }, 0);

  // If no pickSessionItemStatuses, assume all items are unpicked
  if (!group.pickSessionItemStatuses) {
    return totalQuantity;
  }

  const totalPickedItems = group.pickSessionItemStatuses.reduce(
    (acc, status) => {
      return acc + status.quantityPicked;
    },
    0,
  );

  return totalQuantity - totalPickedItems;
}

export function getFulfillmentGroupShipments(
  group: FulfillmentGroup,
): ShipmentPackage[] {
  return filterTruthy([group.shipment, ...(group.additionalShipments ?? [])]);
}

export function getFulfillmentGroupShipmentsCost(
  group: FulfillmentGroup,
): number {
  return getFulfillmentGroupShipments(group).reduce((acc, shipment) => {
    return acc + Number(shipment.boughtRate?.rate ?? 0);
  }, 0);
}

export function getGlobalEOrderId(group: FulfillmentGroup): string | undefined {
  const globalEOrderIds = group.fulfillmentOrders.map(
    (fo) =>
      fo.order?.customAttributes?.find((attr) => attr.key === "GEOrderId")
        ?.value,
  );
  if (globalEOrderIds.every((id) => id === globalEOrderIds[0])) {
    return globalEOrderIds[0] ?? undefined;
  }
  return undefined;
}

/**
 * Validates that all line items in a fulfillment group have the same currency
 * and returns that currency code. Throws if currencies are mixed.
 */
export function getUniformCurrency(group: FulfillmentGroup): Currency {
  const lineItems = group.fulfillmentOrders.flatMap((fo) => fo.lineItems ?? []);

  if (lineItems.length === 0) {
    return Currency.USD;
  }

  const firstCurrency = lineItems[0]!.unitPrice.currency;

  for (const li of lineItems) {
    if (li.unitPrice.currency !== firstCurrency) {
      throw new Error(
        `Mixed currencies in fulfillment group: found ${li.unitPrice.currency} but expected ${firstCurrency}`,
      );
    }
  }

  return firstCurrency;
}

function ensureCarrierAccountId(rate: OutboundRate): OutboundRate {
  if (rate.carrier_account_id) {
    return rate;
  }
  // Any old IB rates that didn't have a carrier_account_id set will now have it
  if (rate.provider === RateProvider.INTERNATIONAL_BRIDGE) {
    return { ...rate, carrier_account_id: IB_CARRIER_ACCOUNT_ID };
  }
  return rate;
}

export function getFulfillmentGroupTrackingCodes(
  group: FulfillmentGroup | FulfillmentGroupData,
): string[] {
  return filterTruthy([
    group.shipment?.tracker?.trackingCode,
    ...(group.additionalShipments?.map((s) => s.tracker?.trackingCode) ?? []),
  ]);
}
